import React, { ReactNode, useCallback, CSSProperties } from 'react';
import {
  ExtendedRefs,
  FloatingContext,
  FloatingFocusManager,
  FloatingPortal,
  useTransitionStyles,
} from '@floating-ui/react';
import { useFocusRing } from '@react-aria/focus';
import classNames from 'classnames';
import { getKeyFromItem } from '../../utils/shared';
import { CSSValueWithLength, getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { AriaFocusProps } from '../../types/common';
import { dropdown_item_group_css, dropdown_item_group_focus_style } from '../dropdown/styles';
import { semantic_colors } from '../../foundation';
import styled from '@emotion/styled';
import { DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT } from './constants';
import { DropdownCompositeInternalOption } from './DropdownCompositeInternalOption';
import { DropdownCompositeOption } from './types';
import { FormField } from '../form';
import { Stack } from '../stack';
import { Button, TextButton } from '../button';
import { IconReset } from '../icons/generated';

export interface DropdownCompositeOptionsProps<OptionValue> {
  className?: string;
  /**
   * 기본값으로 [ZIndexOrder](https://github.com/croquiscom/pds/blob/main/src/styles/zIndex.ts) 규칙을 적용합니다.
   * @default 1030
   */
  zIndex?: number;
  optionWidth?: CSSValueWithLength;
  noDataText?: ReactNode;
  initialFocus?: number;
  options: Array<DropdownCompositeOption<OptionValue>>;
  value?: OptionValue;
  onClose: () => void;
  keyExtractor?: (item: DropdownCompositeOption<OptionValue>, index: number) => string | number;
  onChange?: (value: OptionValue) => void;
  focusIndex?: number | null;
  getFloatingProps: (props: Record<string, unknown>) => Record<string, unknown>;
  getItemProps: (props: Record<string, unknown>) => Record<string, unknown>;
  context: FloatingContext;
  floatingStyles: CSSProperties;
  refs: ExtendedRefs<Element>;
  setRefs: (node: HTMLElement | null, index: number) => void;
  setFocusIndex?: (index: number) => void;
  onSubmit?: () => void;
  onReset?: () => void;
}

export const DropdownCompositeOptions = <OptionValue,>({
  className,
  zIndex = getZIndex('dropdown'),
  initialFocus,
  options,
  optionWidth,
  onClose,
  noDataText,
  value: valueProp,
  unstableFocusVisible,
  keyExtractor = getKeyFromItem,
  onChange,
  focusIndex,
  context,
  getFloatingProps,
  getItemProps,
  floatingStyles,
  refs,
  setRefs,
  setFocusIndex,
  onSubmit,
  onReset,
}: DropdownCompositeOptionsProps<OptionValue> & AriaFocusProps) => {
  const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
  const selectedIndex = options.findIndex(({ value }) => value === valueProp);
  const selectedOption = options[selectedIndex];
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 100,
    initial: {
      opacity: 0,
    },
    common: {
      transitionTimingFunction: 'linear',
    },
  });

  const handleChange = useCallback(
    (value: OptionValue) => {
      onChange?.(value);
    },
    [onChange],
  );

  const internalOptionProps = {
    setRefs,
    selectedIndex,
    focusIndex,
    onChange: handleChange,
    keyExtractor,
    getItemProps,
    width: optionWidth,
  };

  return (
    <>
      {isMounted && (
        <FloatingPortal id={FLOATING_ROOT_ID}>
          <FloatingFocusManager context={context} initialFocus={initialFocus} returnFocus={false}>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                className: classNames('pds-DropdownComposite', className),
                style: {
                  ...floatingStyles,
                  zIndex,
                  outline: 'none',
                  ...styles,
                },
                onKeyDown(event: KeyboardEvent) {
                  if (event.key === 'ArrowUp' && (!focusIndex || focusIndex < 0)) {
                    setFocusIndex?.(options.length - 1);
                  }
                },
              })}
              css={[
                dropdown_item_group_css,
                { padding: 0 },
                { ...((unstableFocusVisible ?? isFocusVisible) && dropdown_item_group_focus_style) },
              ]}
              {...focusProps}
            >
              <SC.Container>
                <SC.Scrollable>
                  {options.length > 0 ? (
                    options.map((option, index) => {
                      return (
                        <DropdownCompositeInternalOption
                          key={index}
                          option={option}
                          index={index}
                          {...internalOptionProps}
                        />
                      );
                    })
                  ) : noDataText ? (
                    <SC.NoDataItem>{noDataText}</SC.NoDataItem>
                  ) : (
                    <SC.NoDataItem width={164}></SC.NoDataItem>
                  )}
                </SC.Scrollable>
                {valueProp && (
                  <SC.DropdownCompositeInput
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                        onSubmit?.();
                      }}
                      onReset={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onReset?.();
                      }}
                      style={{ height: '100%' }}
                    >
                      <Stack direction='column' height='100%' gap={16} p={16} justify='space-between'>
                        <FormField label={selectedOption.label} position='top'>
                          {selectedOption.children}
                        </FormField>
                        <Stack justify={selectedOption.footerExtra ? 'space-between' : 'flex-end'}>
                          {selectedOption.footerExtra}
                          <Stack gap={12} align='center'>
                            <TextButton
                              startIcon={<IconReset />}
                              kind='text'
                              size='medium'
                              type='reset'
                              {...selectedOption.resetButtonProps}
                            >
                              초기화
                            </TextButton>
                            <Button kind='primary' type='submit' {...selectedOption.submitButtonProps}>
                              확인
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </form>
                  </SC.DropdownCompositeInput>
                )}
              </SC.Container>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const SC = {
  Container: styled.div`
    display: flex;
    width: 100%;
    max-height: ${DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT}px;
  `,
  Scrollable: styled.ul`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 122px;
    margin: 0;
    padding: 8px;
    max-height: ${DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT - 16}px;
    overflow: auto;
  `,
  DropdownCompositeInput: styled.div`
    min-width: 352px;
    border-left: 1px solid ${semantic_colors.border.primary};
  `,
  NoDataItem: styled(Stack)`
    color: ${semantic_colors.content.tertiary};
    cursor: default;
    &:hover,
    &:focus,
    &:active {
      background-color: transparent;
    }
  `,
};
